import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FILTER_FLAGS,
  FILTER_LISTS,
  FILTER_PRICES,
  normalizeFilters,
  filterProblems,
  summarizeFilters,
  type FilterList,
  type FilterFlag,
  type FilterPrice,
  type FilterOption,
  type FilterRange,
  type PublishingFilters,
  type PublishingFilterDirectory,
  type PublishingFilterPreview,
} from '../../../api/src/telegram/publishing-filters';
import { api } from '../api/client';
import { Stack, Typography } from '../components/mui-system';
import { SELECTION_ORDERS, type SelectionOrder } from '../../../api/src/telegram/publishing.types';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Не удалось загрузить фильтры');
function SelectFilter({
  label,
  values,
  options,
  disabled,
  single,
  onChange,
}: {
  label: string;
  values: string[];
  options: FilterOption[];
  disabled: boolean;
  single?: boolean;
  onChange: (values: string[]) => void;
}) {
  const selected = useMemo(
    () =>
      values.map(
        (value) =>
          options.find((o) => o.value === value) ?? { value, label: `${value} — недоступно` },
      ),
    [values, options],
  );
  return (
    <Autocomplete
      multiple
      options={options}
      value={selected}
      disabled={disabled}
      freeSolo={false}
      getOptionLabel={(o) => o.label}
      getOptionKey={(o) => o.value}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      onChange={(_, items) =>
        onChange((single && items.length > 1 ? items.slice(-1) : items).map((o) => o.value))
      }
      noOptionsText="Доступных значений нет"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={
            single ? 'Одно значение из справочника' : 'Выбор из справочника; поиск по названию'
          }
        />
      )}
    />
  );
}
function RangeFilter({
  label,
  value,
  hint,
  disabled,
  onChange,
}: {
  label: string;
  value?: FilterRange;
  hint?: string;
  disabled: boolean;
  onChange: (range: FilterRange) => void;
}) {
  return (
    <Stack gap={1}>
      <Typography variant="body2">{label}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        {(['min', 'max'] as const).map((bound) => (
          <TextField
            key={bound}
            fullWidth
            type="number"
            label={`${label} — ${bound === 'min' ? 'от' : 'до'}`}
            value={value?.[bound] ?? ''}
            disabled={disabled}
            slotProps={{ htmlInput: { min: label === 'Возраст' ? 18 : 0, step: 'any' } }}
            onChange={(e) => {
              const next = { ...value };
              if (e.target.value === '') delete next[bound];
              else next[bound] = Number(e.target.value);
              onChange(next);
            }}
          />
        ))}
      </Stack>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Stack>
  );
}

export function PublishingFiltersControl({
  workspaceId,
  sourceRevision,
  cityId,
  disabled,
  value,
  selectionOrder = 'sequential',
  onChange,
}: {
  workspaceId: string;
  sourceRevision: number;
  cityId: number;
  disabled: boolean;
  value: PublishingFilters;
  selectionOrder?: SelectionOrder;
  onChange: (filters: PublishingFilters, order: SelectionOrder) => void;
}) {
  const [open, setOpen] = useState(false),
    [draft, setDraft] = useState(value);
  const [draftOrder, setDraftOrder] = useState(selectionOrder);
  const [directory, setDirectory] = useState<{
    key: string;
    data?: PublishingFilterDirectory;
    error?: string;
  } | null>(null);
  const [reload, setReload] = useState(0);
  const [preview, setPreview] = useState<{
    loading?: boolean;
    data?: PublishingFilterPreview;
    error?: string;
  } | null>(null);
  const directoryRequest = useRef(0),
    previewRequest = useRef(0);
  const active = open ? draft : value;
  const tagsKey = JSON.stringify([...(active.tags ?? [])].sort());
  const key = JSON.stringify([workspaceId, sourceRevision, cityId, tagsKey]);
  const needed = open || Object.keys(value).length > 0;
  useEffect(() => {
    const request = ++directoryRequest.current;
    setDirectory(null);
    if (!needed || !cityId || disabled) return;
    void api
      .publishingFilterDirectory(
        workspaceId,
        cityId,
        sourceRevision,
        JSON.parse(tagsKey) as string[],
      )
      .then((data) => {
        if (request !== directoryRequest.current) return;
        if (
          data.sourceRevision !== sourceRevision ||
          data.cityId !== cityId ||
          JSON.stringify([...data.tags].sort()) !== tagsKey
        )
          throw new Error('Источник или город изменился. Повторите загрузку фильтров');
        setDirectory({ key, data });
      })
      .catch((e: unknown) => {
        if (request === directoryRequest.current) setDirectory({ key, error: message(e) });
      });
    return () => {
      directoryRequest.current++;
    };
  }, [workspaceId, sourceRevision, cityId, tagsKey, key, needed, reload, disabled]);
  useEffect(
    () => () => {
      previewRequest.current++;
    },
    [],
  );
  const current = directory?.key === key ? directory : null;
  let normalized: PublishingFilters = {},
    issues: string[] = [];
  try {
    normalized = normalizeFilters(active);
    if (current?.data) issues = filterProblems(normalized, current.data);
  } catch (e) {
    issues = [message(e)];
  }
  function update(next: PublishingFilters) {
    previewRequest.current++;
    setPreview(null);
    setDraft(next);
  }
  function close() {
    previewRequest.current++;
    setPreview(null);
    setOpen(false);
  }
  function list(key: FilterList, values: string[]) {
    update({ ...draft, [key]: values });
  }
  async function checkSelection() {
    const request = ++previewRequest.current;
    setPreview({ loading: true });
    try {
      const data = await api.publishingFilterPreview(
        workspaceId,
        cityId,
        sourceRevision,
        normalized,
      );
      if (request !== previewRequest.current) return;
      if (data.sourceRevision !== sourceRevision)
        throw new Error('Источник изменился. Повторите проверку');
      setPreview({ data });
    } catch (e) {
      if (request === previewRequest.current) setPreview({ error: message(e) });
    }
  }
  const loading = needed && !current;
  const valid = !issues.length && (!!current?.data || !Object.keys(normalized).length);
  const controlDisabled = disabled || !current?.data;
  const remainingGroups = (kind: 'features' | 'propertyOptions') => {
    const groups = current?.data?.[kind] ?? [];
    const available = new Set(groups.flatMap((g) => g.options.map((o) => o.value)));
    const unknown = (draft[kind] ?? []).filter((v) => !available.has(v));
    return (
      <>
        {groups.map((group) => (
          <SelectFilter
            key={group.key}
            label={group.label}
            options={group.options}
            values={(draft[kind] ?? []).filter((v) => group.options.some((o) => o.value === v))}
            disabled={controlDisabled}
            single={'single' in group && group.single}
            onChange={(values) =>
              list(kind, [
                ...(draft[kind] ?? []).filter((v) => !group.options.some((o) => o.value === v)),
                ...values,
              ])
            }
          />
        ))}
        {!!unknown.length && (
          <SelectFilter
            label={`Недоступные значения: ${FILTER_LISTS[kind]}`}
            options={[]}
            values={unknown}
            disabled={disabled}
            onChange={(values) =>
              list(kind, [...(draft[kind] ?? []).filter((v) => available.has(v)), ...values])
            }
          />
        )}
      </>
    );
  };
  return (
    <Stack gap={1}>
      <Button
        type="button"
        variant="outlined"
        disabled={disabled}
        onClick={() => {
          setDraft(value);
          setDraftOrder(selectionOrder);
          setPreview(null);
          setOpen(true);
        }}
      >
        Фильтры анкет
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
        {summarizeFilters(value, !open ? current?.data : undefined)}
        {selectionOrder === 'random' && ' Порядок: случайный выбор из доступных анкет.'}
      </Typography>
      {!open && needed && current?.error && (
        <Alert severity="warning">Не удалось проверить справочники фильтров: {current.error}</Alert>
      )}
      {!open && !!issues.length && <Alert severity="warning">{issues.join('; ')}</Alert>}
      <Dialog open={open} onClose={close} fullWidth maxWidth="md">
        <DialogTitle>Фильтры анкет</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Typography color="text.secondary">
              Условия применяются только к этому каналу. Изменения сохранятся вместе с настройками
              канала.
            </Typography>
            <TextField
              select
              label="Порядок выбора анкет"
              value={draftOrder}
              disabled={disabled}
              onChange={(e) => setDraftOrder(e.target.value as SelectionOrder)}
              helperText="Случайный выбор учитывает фильтры, срок повторов и доступность анкеты."
            >
              {Object.entries(SELECTION_ORDERS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {loading && <Typography role="status">Загружаются справочники фильтров…</Typography>}
            {current?.error && <Alert severity="error">{current.error}</Alert>}
            <Button
              type="button"
              disabled={disabled || loading}
              onClick={() => {
                previewRequest.current++;
                setPreview(null);
                setReload((n) => n + 1);
              }}
            >
              {current?.error ? 'Повторить загрузку фильтров' : 'Обновить справочники фильтров'}
            </Button>
            {(['tags', 'districts', 'metroStations'] as FilterList[]).map((k) => (
              <SelectFilter
                key={k}
                label={FILTER_LISTS[k]}
                values={draft[k] ?? []}
                options={current?.data?.lists[k] ?? []}
                disabled={controlDisabled}
                onChange={(values) => list(k, values)}
              />
            ))}
            <Typography variant="caption" color="text.secondary">
              Выбранные теги уточняют остальные справочники.
            </Typography>
            <Divider />
            <Typography variant="subtitle1">Признаки анкеты</Typography>
            {(Object.keys(FILTER_FLAGS) as FilterFlag[]).map((k) => (
              <TextField
                key={k}
                select
                label={FILTER_FLAGS[k]}
                disabled={disabled}
                value={draft[k] === undefined ? 'any' : String(draft[k])}
                onChange={(e) => {
                  const next = { ...draft };
                  if (e.target.value === 'any') delete next[k];
                  else next[k] = e.target.value === 'true';
                  update(next);
                }}
              >
                <MenuItem value="any">Любые</MenuItem>
                <MenuItem value="true">Да</MenuItem>
                <MenuItem value="false">Нет</MenuItem>
              </TextField>
            ))}
            <Typography variant="caption" color="text.secondary">
              «Новая анкета» — признак источника. История публикаций и срок повторов учитываются
              отдельно.
            </Typography>
            <Divider />
            <Typography variant="subtitle1">Числовые свойства</Typography>
            {current?.data && !current.data.properties.length && (
              <Typography>Числовых свойств в справочнике нет.</Typography>
            )}
            {[
              ...new Set([
                ...(current?.data?.properties.map((p) => p.value) ?? []),
                ...Object.keys(draft.properties ?? {}),
              ]),
            ].map((slug) => {
              const p = current?.data?.properties.find((p) => p.value === slug);
              return (
                <RangeFilter
                  key={slug}
                  label={p?.label ?? `${slug} — недоступно`}
                  value={draft.properties?.[slug]}
                  disabled={disabled}
                  hint={p ? `В справочнике: ${p.min ?? '—'} … ${p.max ?? '—'}` : undefined}
                  onChange={(r) =>
                    update({ ...draft, properties: { ...draft.properties, [slug]: r } })
                  }
                />
              );
            })}
            <Divider />
            <Typography variant="subtitle1">Характеристики и опции</Typography>
            {remainingGroups('features')}
            {remainingGroups('propertyOptions')}
            {(['languages', 'contactTypes'] as FilterList[]).map((k) => (
              <SelectFilter
                key={k}
                label={FILTER_LISTS[k]}
                values={draft[k] ?? []}
                options={current?.data?.lists[k] ?? []}
                disabled={controlDisabled}
                onChange={(values) => list(k, values)}
              />
            ))}
            <Divider />
            <Typography variant="subtitle1">Цены</Typography>
            <Typography variant="caption" color="text.secondary">
              В валюте источника. Пустая граница означает отсутствие ограничения.
            </Typography>
            {(Object.keys(FILTER_PRICES) as FilterPrice[]).map((k) => (
              <RangeFilter
                key={k}
                label={FILTER_PRICES[k].label}
                value={draft.prices?.[k]}
                disabled={disabled}
                onChange={(r) => update({ ...draft, prices: { ...draft.prices, [k]: r } })}
              />
            ))}
            {!!issues.length && <Alert severity="error">{issues.join('; ')}</Alert>}
            <Button
              type="button"
              disabled={disabled || !valid || !current?.data || preview?.loading}
              onClick={() => void checkSelection()}
            >
              {preview?.loading ? 'Проверяем подборку…' : 'Проверить подборку'}
            </Button>
            {preview?.error && <Alert severity="error">{preview.error}</Alert>}
            {preview?.data && (
              <Alert severity={preview.data.total ? 'info' : 'warning'} role="status">
                Найдено у источника: {preview.data.total}. Доступных к публикации может быть меньше
                из-за состояния анкет, фотографий и срока повторов.
                {!!preview.data.sample.length && (
                  <Typography>
                    Примеры: {preview.data.sample.map((p) => `${p.name} (#${p.id})`).join(', ')}.
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button type="button" disabled={disabled} onClick={() => update({})}>
            Сбросить фильтры
          </Button>
          <Button type="button" onClick={close}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={disabled || !valid}
            onClick={() => {
              onChange(normalized, draftOrder);
              close();
            }}
          >
            Применить фильтры
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
